// utils/sessionManager.js
// ============================================
// SESSION MANAGER - Handles multi-tab sessions
// ============================================

const SESSION_KEY = 'app_session_id';
const SESSION_DATA_PREFIX = 'session_';
const SESSION_HEARTBEAT_KEY = 'session_heartbeat_';
const HEARTBEAT_INTERVAL = 5000; // 5 seconds
const SESSION_TIMEOUT = 900000; // 15 minutes

class SessionManager {
   constructor() {
    this.currentSessionId = null;
    this.heartbeatInterval = null;
    this.storageListener = null;
    this.isInitialized = false;

    this.cleanupStaleSessions();
    this.initSession();
  }

  initSession() {
    if (this.isInitialized) {
      return this.currentSessionId;
    }

    // Check if there's an active session in another tab
    const existingSession = this.getActiveSession();
    
    if (existingSession) {
      this.currentSessionId = existingSession.sessionId;
    } else {
      this.currentSessionId = this.generateSessionId();
    }

    sessionStorage.setItem(SESSION_KEY, this.currentSessionId);
    this.startHeartbeat();
    this.setupStorageListener();
    
    this.isInitialized = true;

    return this.currentSessionId;
  }

  // Generate unique session ID
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  initData(userData) {
    this.initSession();
    // store session data in localStorage
    this.setSessionData(userData);
  }

  // Get active session from any tab
  getActiveSession() {
    const now = Date.now();
    const keys = Object.keys(localStorage);

    for (const key of keys) {
      if (key.startsWith(SESSION_HEARTBEAT_KEY)) {
        const sessionId = key.replace(SESSION_HEARTBEAT_KEY, '');
        const heartbeat = parseInt(localStorage.getItem(key), 10);

        // Check if heartbeat is recent (session is active)
        if (now - heartbeat < SESSION_TIMEOUT) {
          const sessionData = this.getSessionDataById(sessionId);
          if (sessionData) {
            return { sessionId, ...sessionData };
          }
        } else {
          // Clean up stale session
          this.cleanupSession(sessionId);
        }
      }
    }

    return null;
  }

  // Set session data
  setSessionData(userData) {
    const sessionData = {
      ...userData,
      timestamp: Date.now(),
    };
    localStorage.setItem(
      `${SESSION_DATA_PREFIX}${this.currentSessionId}`,
      JSON.stringify(sessionData)
    );
  }

  // Get session data by ID
  getSessionDataById(sessionId) {
    const data = localStorage.getItem(`${SESSION_DATA_PREFIX}${sessionId}`);
    return data ? JSON.parse(data) : null;
  }

  // Get current session data
  getSessionData() {
    if (!this.currentSessionId) {
      const storedSessionId = sessionStorage.getItem(SESSION_KEY);
      if (storedSessionId) {
        this.currentSessionId = storedSessionId;
      }
    }

    if (this.currentSessionId) {
      const sessionData = this.getSessionDataById(this.currentSessionId);
      if (sessionData) {
        return sessionData;
      }
    }

    // ✅ NEW: Fallback to localStorage user data if session is not found
    const fallbackUser = localStorage.getItem('user');
    if (fallbackUser) {
      try {
        const parsedUser = JSON.parse(fallbackUser);
        return {
          username: parsedUser.username || localStorage.getItem('employeeName'),
          email: localStorage.getItem('employeeEmail'),
          role: localStorage.getItem('role'),
          ...parsedUser
        };
      } catch (e) {
        console.error('Error parsing fallback user:', e);
      }
    }

    // ✅ NEW: Final fallback - construct from individual localStorage items
    const token = localStorage.getItem('token');
    if (token) {
      return {
        username: localStorage.getItem('employeeName') || 
                  localStorage.getItem('employeeEmail')?.split('@')[0] || 
                  'User',
        email: localStorage.getItem('employeeEmail'),
        role: localStorage.getItem('role'),
        token: token
      };
    }

    return null;
  }

  // Start heartbeat to keep session alive
  startHeartbeat() {
    this.updateHeartbeat();

    this.heartbeatInterval = setInterval(() => {
      this.updateHeartbeat();
    }, HEARTBEAT_INTERVAL);
  }

  // Update heartbeat timestamp
  updateHeartbeat() {
    if (this.currentSessionId) {
      localStorage.setItem(
        `${SESSION_HEARTBEAT_KEY}${this.currentSessionId}`,
        Date.now().toString()
      );
    }
  }

  // Setup storage listener for cross-tab communication
  setupStorageListener() {
    this.storageListener = (e) => {
      // Check if another tab logged out
      if (e.key === `${SESSION_DATA_PREFIX}${this.currentSessionId}` && !e.newValue) {
        // Session was cleared by another tab
        this.handleSessionClear();
      }
    };

    window.addEventListener('storage', this.storageListener);

    // Also handle tab close/refresh
    window.addEventListener('beforeunload', () => {
      this.updateHeartbeat(); // One last heartbeat
    });
  }

  // Handle session cleared by another tab
  handleSessionClear() {
    this.cleanup();
    window.location.href = '/login';
  }

  // Check if user is authenticated
  isAuthenticated() {
    const activeSession = this.getActiveSession();
    
    if (activeSession) {
      // Update current session ID if needed
      if (this.currentSessionId !== activeSession.sessionId) {
        this.currentSessionId = activeSession.sessionId;
        sessionStorage.setItem(SESSION_KEY, this.currentSessionId);
      }
      return true;
    }

    // ✅ ENHANCED: Check localStorage for valid token or user data (fallback)
    const token = localStorage.getItem('token');
    const localUser = localStorage.getItem('user');
    const sessionUser = sessionStorage.getItem('user');
    
    return !!(token || localUser || sessionUser);
  }

  // Get user role
  getUserRole() {
    const sessionData = this.getSessionData();
    if (sessionData && sessionData.role) {
      return sessionData.role;
    }

    // Fallback to localStorage
    return localStorage.getItem('role');
  }

  // ✅ NEW: Get user data (for compatibility with AuthContext)
  getUser() {
    const sessionData = this.getSessionData();
    if (sessionData) {
      return {
        username: sessionData.username || sessionData.employeeName,
        email: sessionData.email || sessionData.employeeEmail,
        role: sessionData.role,
        ...sessionData
      };
    }

    // Fallback to localStorage 'user' key
    const localUser = localStorage.getItem('user');
    const sessionUser = sessionStorage.getItem('user');
    
    if (localUser) {
      try {
        return JSON.parse(localUser);
      } catch (e) {
        console.error('Error parsing local user:', e);
      }
    }
    
    if (sessionUser) {
      try {
        return JSON.parse(sessionUser);
      } catch (e) {
        console.error('Error parsing session user:', e);
      }
    }

    return null;
  }

  // Cleanup session for current tab
  cleanup() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }

    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
      this.storageListener = null;
    }

    // Remove heartbeat
    if (this.currentSessionId) {
      localStorage.removeItem(`${SESSION_HEARTBEAT_KEY}${this.currentSessionId}`);
    }

    // Clear session storage for this tab
    sessionStorage.removeItem(SESSION_KEY);
  }

  // Cleanup specific session
  cleanupSession(sessionId) {
    localStorage.removeItem(`${SESSION_DATA_PREFIX}${sessionId}`);
    localStorage.removeItem(`${SESSION_HEARTBEAT_KEY}${sessionId}`);
  }

  // Logout - clear all sessions
  logout() {
    // Remove current session data directly (O(1) — no full localStorage scan)
    if (this.currentSessionId) {
      localStorage.removeItem(`${SESSION_DATA_PREFIX}${this.currentSessionId}`);
      localStorage.removeItem(`${SESSION_HEARTBEAT_KEY}${this.currentSessionId}`);
    }

    // Clear standard auth data
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('email');
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');

    // Cleanup current session (clears interval + listeners)
    this.cleanup();
  }

  // Clean up all stale sessions
  cleanupStaleSessions() {
    const now = Date.now();
    const keys = Object.keys(localStorage);

    keys.forEach(key => {
      if (key.startsWith(SESSION_HEARTBEAT_KEY)) {
        const sessionId = key.replace(SESSION_HEARTBEAT_KEY, '');
        const heartbeat = parseInt(localStorage.getItem(key), 10);

        if (now - heartbeat >= SESSION_TIMEOUT) {
          this.cleanupSession(sessionId);
        }
      }
    });
  }
}

// Export singleton instance
export const sessionManager = new SessionManager();